/**
 * Re-apply dashboard sort rules for CSV export (client path).
 * Mirrors useFilteredAndSortedRowsGeneric + phone dimension keys.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { GenericStatsRow } from '../shared/generic-stats.types';
import { getVoluumValueForDisplay } from '../shared';

function splitByWorkDate(
  rows: GenericStatsRow[],
  workDate: string,
): { workDayRows: GenericStatsRow[]; otherRows: GenericStatsRow[] } {
  const workDayRows: GenericStatsRow[] = [];
  const otherRows: GenericStatsRow[] = [];
  for (const r of rows) {
    if (r.workDateIso === workDate) workDayRows.push(r);
    else otherRows.push(r);
  }
  return { workDayRows, otherRows };
}

function compareVoluumField(
  a: GenericStatsRow,
  b: GenericStatsRow,
  field: string,
  mult: number,
  ia: number,
  ib: number,
): number {
  const aVal = getVoluumValueForDisplay(a.voluum, field);
  const bVal = getVoluumValueForDisplay(b.voluum, field);
  const aNum = typeof aVal === 'number' ? aVal : null;
  const bNum = typeof bVal === 'number' ? bVal : null;
  let cmp: number;
  if (aNum != null && bNum != null) cmp = (aNum - bNum) * mult;
  else if (aNum != null) cmp = -1;
  else if (bNum != null) cmp = 1;
  else cmp = 0;
  if (cmp !== 0) return cmp;
  const idCmp = (a.id ?? '').localeCompare(b.id ?? '');
  if (idCmp !== 0) return idCmp;
  return ia - ib;
}

export function sortGenericStatsRowsForCsvExport(
  rows: GenericStatsRow[],
  sort: { field: string; dir: 'asc' | 'desc' },
  workDate: string,
): GenericStatsRow[] {
  const { field, dir } = sort;
  const mult = dir === 'asc' ? 1 : -1;
  const { workDayRows, otherRows } = splitByWorkDate(rows, workDate);

  if (field === 'dropList') {
    const bySeq = (a: GenericStatsRow, b: GenericStatsRow) => {
      const seqA = a.sequence ?? 999999;
      const seqB = b.sequence ?? 999999;
      if (seqA !== seqB) return (seqA - seqB) * mult;
      return (a.label ?? '').localeCompare(b.label ?? '') * mult;
    };
    return [...workDayRows].sort(bySeq).concat([...otherRows].sort(bySeq));
  }

  const labelFields = [
    'list',
    'gateway',
    'source',
    'text',
    'path',
    'lander',
    'offer',
  ];
  if (labelFields.includes(field)) {
    const byLabel = (a: GenericStatsRow, b: GenericStatsRow) =>
      (a.label ?? a.subLabel ?? '').localeCompare(b.label ?? b.subLabel ?? '') *
      mult;
    return [...workDayRows].sort(byLabel).concat([...otherRows].sort(byLabel));
  }

  if (field === 'phone') {
    const byPhone = (a: GenericStatsRow, b: GenericStatsRow) =>
      (a.label ?? '').localeCompare(b.label ?? '') * mult;
    return [...workDayRows].sort(byPhone).concat([...otherRows].sort(byPhone));
  }

  if (field === 'sourceId') {
    const src = (r: GenericStatsRow) =>
      r._originalPhone?.sourceId ?? r.subLabel ?? '';
    const bySrc = (a: GenericStatsRow, b: GenericStatsRow) =>
      src(a).localeCompare(src(b)) * mult;
    return [...workDayRows].sort(bySrc).concat([...otherRows].sort(bySrc));
  }

  if (field === 'country' || field === 'countryIso') {
    const byId = (a: GenericStatsRow, b: GenericStatsRow) =>
      (a.id ?? '').localeCompare(b.id ?? '') * mult;
    return [...workDayRows].sort(byId).concat([...otherRows].sort(byId));
  }

  const combined = workDayRows.concat(otherRows);
  const withIndex = combined.map((r, i) => ({ r, i }));
  return withIndex
    .sort((a, b) =>
      compareVoluumField(a.r, b.r, field, mult, a.i, b.i),
    )
    .map((x) => x.r);
}
